import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { AppStorage, LiveEventAction, LiveEventActionError, UserRole } from 'types';
import { eventSessionSelector, userSelector } from 'store/selectors';
import { LoggerService, storage } from 'services';
import { useLiveEventActions, useStore } from 'hooks';
import { useLiveEvent } from 'containers/LiveEvent';
import { Tooltip } from 'components';
import {
  ActiveSpeakerModeIcon,
  CameraOffNewIcon,
  CameraOnNewIcon,
  GridModeIcon,
  MicrophoneOffNewIcon,
  MicrophoneOnNewIcon,
  PeopleIcon,
  PiPModeIcon,
  RaiseHandClosedIcon,
  RaiseHandIcon,
  ShareIcon
} from 'components/Icon';

import styles from './ControlPanel.module.scss';

export type ControlPanelProps = {
  gestureCaptured?: boolean;
  usersCount?: number;
  requestsCount?: number;
  audioMuted?: boolean;
  videoMuted?: boolean;
  handRaised?: boolean;
  isForceMuteTooltipVisible?: boolean;
  isHandRaiseEnabled?: boolean;
  isPassiveViewer?: boolean;
  isLayoutSwitchAvailable?: boolean;
  gridViewEnabled?: boolean;
  onToggleAudio?(): Promise<void>;
  onToggleVideo?(): Promise<void>;
  onOpenGuests?(): void;
  onRaiseHand?(): Promise<void>;
  onDropHand?(): Promise<void>;
  onOpenShare?(): void;
  onOpenRequests?(): void;
  onHideForceMuteTooltip?(): void;
  onChangeLayout?(): void;
};

const HAND_RAISE_TOOLTIP_MESSAGE = {
  FIRST_MENTION: 'Hey, You can raise your hand here to speak',
  OPENED: 'Hand raise is opened',
  CLOSED: 'Hand raise is closed',
  MEDIA_ATTEMPT: 'Camera and video will be enabled only once you join the stage'
};

const ControlPanel = ({
  gestureCaptured = false,
  usersCount = 0,
  requestsCount = 0,
  audioMuted = false,
  videoMuted = false,
  handRaised = false,
  isForceMuteTooltipVisible = false,
  isHandRaiseEnabled = false,
  isPassiveViewer = false,
  isLayoutSwitchAvailable = false,
  gridViewEnabled = false,
  onToggleAudio,
  onToggleVideo,
  onOpenGuests,
  onRaiseHand,
  onDropHand,
  onOpenShare,
  onOpenRequests,
  onHideForceMuteTooltip,
  onChangeLayout
}: ControlPanelProps): JSX.Element => {
  const liveEvent = useLiveEvent();
  const eventSession = useStore(eventSessionSelector(liveEvent.eventCode));
  const localUser = useStore(userSelector(liveEvent.eventCode, eventSession.username));
  const liveEventActions = useLiveEventActions(liveEvent.eventCode);

  const [state, setState] = useState({
    isHandRaiseTooltipVisible: false,
    isRequestsAnimationVisible: false,
    audioIsLoading: false,
    videoIsLoading: false,
    handRaiseIsLoading: false,
    tooltipContent: ''
  });
  const requestsCountRef = useRef(requestsCount);

  // show hand raise tooltip initially effect
  useEffect(() => {
    const handRaiseTooltipShown = storage.local.get(AppStorage.Local.HAND_RAISE_TOOLTIP_SHOWN) === 'true';

    if (gestureCaptured && !handRaiseTooltipShown) {
      showHandRaiseTooltip(HAND_RAISE_TOOLTIP_MESSAGE.FIRST_MENTION);
    }
  }, [gestureCaptured]);

  // hide hand-raise tooltip when user becomes speaker effect
  useEffect(() => {
    if (localUser.role !== UserRole.VIEWER) {
      hideHandRaiseTooltip();
    }
  }, [localUser.role]);

  // hide hand-raise tooltip when hand-raise request is sent effect
  useEffect(() => {
    if (handRaised) {
      hideHandRaiseTooltip();
    }
  }, [handRaised]);

  // toggle requests button pulse animation on requestsCount increment
  useEffect(() => {
    if ([UserRole.HOST, UserRole.MODERATOR].includes(localUser.role)) {
      if (requestsCount > requestsCountRef.current) {
        toggleRequestsAnimation(true);

        setTimeout(() => toggleRequestsAnimation(false), 9_000);
      }
      requestsCountRef.current = requestsCount;
    }
  }, [requestsCount]);

  // toggle hand-raise button pulse animation for viewers when enabled by the host
  useEffect(() => {
    const isViewer = localUser.role === UserRole.VIEWER;
    const isFirstMention = !gestureCaptured || state.tooltipContent === HAND_RAISE_TOOLTIP_MESSAGE.FIRST_MENTION;

    if (!isFirstMention && isViewer) {
      if (isHandRaiseEnabled) {
        toggleRequestsAnimation(true);
        showHandRaiseTooltip(HAND_RAISE_TOOLTIP_MESSAGE.OPENED);
      } else {
        toggleRequestsAnimation(false);
        showHandRaiseTooltip(HAND_RAISE_TOOLTIP_MESSAGE.CLOSED);
      }

      const tooltipTimeoutId = setTimeout(() => hideHandRaiseTooltip(), 3_000);
      const animationTimeoutId = setTimeout(() => toggleRequestsAnimation(false), 3_000);

      return () => {
        clearTimeout(tooltipTimeoutId);
        clearTimeout(animationTimeoutId);
      };
    }

    return () => {};
  }, [isHandRaiseEnabled]);

  const showHandRaiseTooltip = (content: string) =>
    setState(prev => ({ ...prev, isHandRaiseTooltipVisible: true, tooltipContent: content }));

  const hideHandRaiseTooltip = () => {
    const handRaiseTooltipShown = storage.local.get(AppStorage.Local.HAND_RAISE_TOOLTIP_SHOWN) === 'true';

    if (!handRaiseTooltipShown) {
      storage.local.set(AppStorage.Local.HAND_RAISE_TOOLTIP_SHOWN, 'true');
    }

    setState(prev => ({ ...prev, isHandRaiseTooltipVisible: false, tooltipContent: '' }));
  };

  const handleOnToggleAudio = async () => {
    if (localUser.role === UserRole.VIEWER) {
      showHandRaiseTooltip(HAND_RAISE_TOOLTIP_MESSAGE.MEDIA_ATTEMPT);
    } else {
      setState(prev => ({ ...prev, audioIsLoading: true }));
      await onToggleAudio?.();
      setState(prev => ({ ...prev, audioIsLoading: false }));

      if (audioMuted && isForceMuteTooltipVisible) {
        onHideForceMuteTooltip?.();
      }
    }
  };

  const handleOnToggleVideo = async () => {
    if (localUser.role === UserRole.VIEWER) {
      showHandRaiseTooltip(HAND_RAISE_TOOLTIP_MESSAGE.MEDIA_ATTEMPT);
    } else {
      setState(prev => ({ ...prev, videoIsLoading: true }));
      await onToggleVideo?.();
      setState(prev => ({ ...prev, videoIsLoading: false }));
    }
  };

  const handleOnToggleHandRaise = async () => {
    if (!isHandRaiseEnabled) {
      showHandRaiseTooltip(HAND_RAISE_TOOLTIP_MESSAGE.CLOSED);
      return;
    }

    if (handRaised) {
      handleOnDropHand();
    } else {
      handleOnRaiseHand();
    }
  };

  const handleOnRaiseHand = async () => {
    try {
      await liveEventActions.attempt(LiveEventAction.RAISE_HAND);
      setState(prev => ({ ...prev, handRaiseIsLoading: true }));
      await onRaiseHand?.();
      setState(prev => ({ ...prev, handRaiseIsLoading: false }));
    } catch (e) {
      LoggerService.log('HANDLE_ON_RAISE_HAND', e);
      if (e instanceof Error) {
        switch (e.message) {
          case LiveEventActionError.AUTH_REQUIRED: {
            liveEvent.openLoginPanel(LiveEventAction.RAISE_HAND);
            break;
          }
        }
      }
    }
  };

  const handleOnDropHand = async () => {
    try {
      await liveEventActions.attempt(LiveEventAction.DROP_HAND);
      setState(prev => ({ ...prev, handRaiseIsLoading: true }));
      await onDropHand?.();
      setState(prev => ({ ...prev, handRaiseIsLoading: false }));
    } catch (e) {
      LoggerService.log('HANDLE_ON_DROP_HAND', e);
      if (e instanceof Error) {
        switch (e.message) {
          case LiveEventActionError.AUTH_REQUIRED: {
            liveEvent.openLoginPanel(LiveEventAction.DROP_HAND);
            break;
          }
        }
      }
    }
  };

  const handleOnTogglePictureInPictureMode = () => {
    if (liveEvent.isPictureInPictureModeEnabled) {
      liveEvent.disablePictureInPictureMode(true);
    } else {
      liveEvent.enablePictureInPictureMode(true);
    }
  };

  const toggleRequestsAnimation = (isVisible: boolean) => {
    setState(prev => ({ ...prev, isRequestsAnimationVisible: isVisible }));
  };

  const renderStudioPanelButton = () => (
    <button type="button" className={styles.users} onClick={onOpenGuests}>
      <PeopleIcon />
      <span>{usersCount}</span>
    </button>
  );

  const renderCenterButton = () => {
    if (isPassiveViewer) {
      if (document.pictureInPictureEnabled) return renderPiPButton();

      return null;
    }

    return renderLayoutButton();
  };

  const renderPiPButton = () => (
    <button
      type="button"
      className={cx(styles.pip, {
        [styles.active]: liveEvent.isPictureInPictureModeEnabled
      })}
      disabled={!gestureCaptured}
      onClick={handleOnTogglePictureInPictureMode}
    >
      <PiPModeIcon />
    </button>
  );

  const renderLayoutButton = () =>
    isLayoutSwitchAvailable && (
      <button type="button" className={styles.layout} onClick={onChangeLayout}>
        {gridViewEnabled ? <ActiveSpeakerModeIcon /> : <GridModeIcon />}
      </button>
    );

  const renderShareWithFriends = () => (
    <button type="button" className={styles.share} onClick={onOpenShare}>
      <ShareIcon />
    </button>
  );

  const renderRequestsBadge = () => {
    if ([UserRole.HOST, UserRole.MODERATOR].includes(localUser.role)) {
      return <>{!!requestsCount && <span className={styles.badge}>{requestsCount}</span>}</>;
    }
    return null;
  };

  const renderRequestsButton = () => (
    <button
      type="button"
      className={cx(styles.requests, {
        [styles.closed]: !isHandRaiseEnabled,
        [styles.pulse]: state.isRequestsAnimationVisible
      })}
      onClick={onOpenRequests}
    >
      {renderRequestsBadge()}
      {isHandRaiseEnabled ? <RaiseHandIcon /> : <RaiseHandClosedIcon />}
    </button>
  );

  const renderRaiseHandButton = () => {
    switch (localUser.role) {
      case UserRole.HOST:
      case UserRole.MODERATOR:
        return renderRequestsButton();

      case UserRole.VIEWER:
        return (
          <Tooltip
            placement="top"
            visible={state.isHandRaiseTooltipVisible}
            title={<div>{state.tooltipContent}</div>}
            withArrow
            onClose={hideHandRaiseTooltip}
          >
            {ref => (
              <button
                ref={ref}
                type="button"
                className={cx(styles.request, {
                  [styles.closed]: !isHandRaiseEnabled,
                  [styles.sent]: handRaised,
                  [styles.pulse]: state.isRequestsAnimationVisible
                })}
                disabled={state.handRaiseIsLoading}
                onClick={handleOnToggleHandRaise}
              >
                {isHandRaiseEnabled ? <RaiseHandIcon /> : <RaiseHandClosedIcon />}
              </button>
            )}
          </Tooltip>
        );

      default:
        return null;
    }
  };

  const renderMicrophoneButton = () => (
    <Tooltip
      placement="top"
      visible={isForceMuteTooltipVisible}
      title={<div>You have been muted. You can unmute yourself anytime.</div>}
      withArrow
      onClose={onHideForceMuteTooltip}
    >
      {ref => (
        <button
          ref={ref}
          type="button"
          disabled={state.audioIsLoading}
          className={cx(styles.mic, {
            [styles.muted]: audioMuted
          })}
          onClick={handleOnToggleAudio}
        >
          {audioMuted ? <MicrophoneOffNewIcon /> : <MicrophoneOnNewIcon />}
        </button>
      )}
    </Tooltip>
  );

  const renderCameraButton = () => (
    <button
      type="button"
      disabled={state.videoIsLoading}
      className={cx(styles.cam, {
        [styles.muted]: videoMuted
      })}
      onClick={handleOnToggleVideo}
    >
      {videoMuted ? <CameraOffNewIcon /> : <CameraOnNewIcon />}
    </button>
  );

  const renderLeftButtons = () => {
    switch (localUser.role) {
      case UserRole.HOST:
      case UserRole.MODERATOR:
      case UserRole.VIEWER:
        return (
          <>
            {renderStudioPanelButton()}
            {renderRaiseHandButton()}
          </>
        );
      case UserRole.SPEAKER:
        return <>{renderStudioPanelButton()}</>;
      default:
        return null;
    }
  };

  const renderRightButtons = () => (
    <>
      {renderMicrophoneButton()}
      {renderCameraButton()}
      {renderShareWithFriends()}
    </>
  );

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.left}>{renderLeftButtons()}</div>
        <div className={styles.center}>{renderCenterButton()}</div>
        <div className={styles.right}>{renderRightButtons()}</div>
      </div>
    </div>
  );
};

export default ControlPanel;
